import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, ReplaySubject } from 'rxjs';
import { catchError, filter, map, tap } from 'rxjs/operators';

import { addAdditionalFields } from '../shared/utils';
import { DomainKey, Product } from '../../../shared/types/product';
import { ActivatedRoute, NavigationEnd, Router, Params } from '@angular/router';
import { DOMAIN_KEYS, PRODUCT_PAGE_SIZE } from '../../../shared/constants';

@Injectable({ providedIn: 'root' })
export class SearchService {
  private lastSearch = 'gsdgfdgfdsgreggsergrsegs';
  private readonly searchTermSubject = new ReplaySubject<string>(1);
  readonly searchTerm$ = this.searchTermSubject.asObservable();
  readonly products = signal<Product[]>([]);
  readonly endOfStream = signal(false);
  readonly getProductsLoading = signal(false);
  readonly getNextPageLoading = signal(false);
  private productsUrl = 'api/products';
  private previousParams: { [key: string]: string | string[] } = {};

  httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
  };

  constructor(
    private http: HttpClient,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.rootRoute(this.activatedRoute)),
      filter((route: ActivatedRoute) => route.outlet === 'primary'),
    ).subscribe((route: ActivatedRoute) => {
      if (this.router.url.indexOf('/deals') >= 0) {
        this.getProducts(route.snapshot.queryParams);
      }
    });
  }

  private rootRoute(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  /** GET products from the server */
  getProducts(params: Params) {
    if (
      typeof params['search'] === 'string' &&
      params['search'] !== this.lastSearch
    ) {
      this.lastSearch = params['search'];
      this.searchTermSubject.next(params['search']);
    }
    this.getProductsLoading.set(true);
    this.endOfStream.set(false);
    let apiUrl = this.productsUrl;
    this.http
      .get<Product[]>(apiUrl, { params })
      .pipe(
        tap(() => {
          this.getProductsLoading.set(false);
        }),
        catchError(this.handleError<Product[]>('getproducts', []))
      )
      .subscribe((products) => {
        this.previousParams = { ...params };
        if (products.length < PRODUCT_PAGE_SIZE) {
          this.endOfStream.set(true);
        } else {
          this.endOfStream.set(false);
        }
        let domain: DomainKey = '1';
        const domainParam = params['domain'] as string;
        if (domainParam && DOMAIN_KEYS.indexOf(domainParam as any) >= 0) {
          domain = domainParam as DomainKey;
        }
        this.products.set(
          products.map((product) => addAdditionalFields(product, domain))
        );
      });
  }

  getNextPage() {
    this.getNextPageLoading.set(true);
    const params = { ...this.previousParams };
    const lastProducts = this.products();
    if (lastProducts.length) {
      const lastProduct = lastProducts[lastProducts.length - 1];
      params.a = String(lastProduct.asin);
      params.p = String(lastProduct.pricedifference);
      params.s = String(lastProduct.savingspercent);
      params.d = String(lastProduct.dealscore);
      params.u = String(lastProduct.usedprice);
      params.l = String(lastProduct.lastchange);
    }
    this.http
      .get<Product[]>(this.productsUrl, { params })
      .pipe(
        tap(() => {
          this.getNextPageLoading.set(false);
        }),
        catchError(this.handleError<Product[]>('getproducts', []))
      )
      .subscribe((products) => {
        if (products.length < PRODUCT_PAGE_SIZE) {
          this.endOfStream.set(true);
        } else {
          this.endOfStream.set(false);
        }
        this.previousParams = params;
        let domain: DomainKey = '1';
        const domainParam = params['domain'] as string;
        if (domainParam && DOMAIN_KEYS.indexOf(domainParam as any) >= 0) {
          domain = domainParam as DomainKey;
        }
        this.products.set(
          lastProducts.concat(
            products.map((product) =>
              addAdditionalFields(product, domain)
            )
          )
        );
      });
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.error(error);
      return of(result as T);
    };
  }
}
